import { useReducer, useEffect, useContext, useCallback } from 'react'
import {
    UniversalDataTable,
    UniversalModal,
    UniversalToolBar,
    UniversalLoadingCircle,
    UniversalExcelExport,
    Grid,
    FormSection,
} from '../../../UniversalComponents'
import { IconButton, TextField, Tooltip } from '@mui/material'
import {
    PersonAdd as PersonAddIcon,
    Delete as DeleteIcon,
    Archive as ArchiveIcon,
    ArrowOutward as ArrowOutwardIcon,
    Restore,
    DownloadForOffline,
    AssignmentInd,
} from '@mui/icons-material'
import {
    getUsers,
    activateUsers,
    deleteUsers,
    getUsersWithLimiter,
} from '../../../../apiRoutes'
import { ToastMessage } from '../../../../context/ToastPopUpContext'
import { useMinimizer } from '../../../../context/MinimizeContext'
import { useSocket } from '../../../../context/SocketContext'
import {
    Authentication,
    useAuth,
} from '../../../../context/AuthenticationContext'
import {
    standardColumnMapper,
    permissionsCheck,
    handleDeletion,
    useDebounce,
    universalDateConverter,
} from '../../../../utilities/functionTools'
import UserForm from './UserForm'
import UserRegistration from '../UserRegistration/UserRegistrationForm'
import { useGlobalState } from '../../../../context/GlobalStateContext'

const today = new Date().toISOString().substring(0, 10)

const UserAdmin = () => {
    const currentUser = useContext(Authentication)
    const { setUser } = useAuth()
    const { username, userPreferences, userProfile } = currentUser.auth
    const { superAdmin, userProfilePermissions } = userProfile
    const { globalState } = useGlobalState()
    const { oupUser } = globalState
    const { activeManufacturer, userEmail } = oupUser

    const defaultState = {
        open: false,
        row: {},
        formCache: {},
        users: [],
        data: [],
        openFileNamer: false,
        openUserRegistrationModal: false,
        usersToActivate: [],
        activateData: [],
        viewDeactivated: false,
        excelExportName: '',
        excelSelectedRows: [],
        manufacturers: [],
        loading: false,
        paginationModel: { page: 0, pageSize: 50 },
        apiSearch: '',
        rowCount: 0,
        noResults: false,
        userColumns: {
            userAdmin: {
                columns: [
                    {
                        field: 'userEmail',
                        headerName: 'EMAIL',
                        width: 150,
                    },
                    {
                        field: 'userFirstName',
                        headerName: 'FIRST NAME',
                        width: 150,
                    },
                    {
                        field: 'userLastName',
                        headerName: 'LAST NAME',
                        width: 150,
                    },
                    {
                        field: 'userPhoneNumber',
                        headerName: 'PHONE NUMBER',
                        width: 150,
                    },
                    {
                        field: 'manufacturerName',
                        headerName: 'MANUFACTURER',
                        width: 150,
                    },
                ],
                density: 'standard',
            },
        },
        density:
            userPreferences?.dataTableColumns?.users?.userAdmin?.density ||
            'standard',
    }
    const setToastMessage = useContext(ToastMessage)
    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)
    const { setMinimized } = useMinimizer()
    const { apiSocket } = useSocket()

    const {
        open,
        row,
        formCache,
        users,
        openFileNamer,
        excelExportName,
        usersToActivate,
        viewDeactivated,
        excelSelectedRows,
        userColumns,
        density,
        activateData,
        data,
        loading,
        paginationModel,
        apiSearch,
        rowCount,
        noResults,
    } = state
    const { userAdmin } = userColumns

    const userFormPermissions = superAdmin && superAdmin === true
    const toggleUserRegistrationModal = () =>
        setState({
            openUserRegistrationModal: !state.openUserRegistrationModal,
        })

    const debouncedSearchQuery = useDebounce(apiSearch, 1000)

    const onApiSearch = useCallback((query: any) => {
        setState({
            apiSearch: query,
        })
        if (query !== null) {
            setState({ paginationModel: { ...paginationModel, page: 0 } })
        }
    }, [])

    const getData = async (active) => {
        setState({ loading: true })
        let userData: any
        try {
            const users = await getUsersWithLimiter({
                active,
                page: paginationModel?.page + 1,
                limit: 50,
                filter: debouncedSearchQuery
                    ? JSON.stringify(debouncedSearchQuery)
                    : undefined,
            })

            userData = Array.isArray(users?.users) ? users.users : []
            const total = Number(users?.total) || 0

            if (
                userData?.length === 0 &&
                total === 0 &&
                debouncedSearchQuery !== null
            ) {
                setToastMessage(
                    'No Users found - Please Try another Search',
                    'info'
                )
                setState({
                    apiSearch: null,
                    paginationModel: { page: 0, pageSize: 50 },
                    rowCount: total,
                    loading: false,
                    noResults: true,
                })
                return
            }
            userData = standardColumnMapper(defaultColumns, users?.users).map(
                (user: any) => ({
                    ...user,
                    id: user?._id,
                })
            )

            setState({
                data: userData,
                users: userData,
                rowCount: total,
                loading: false,
                noResults: false,
            })
        } catch (err: any) {
            let errorMessage = 'An unknown error occurred'

            if (err.response?.data?.message) {
                errorMessage = err.response.data.message
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error
            } else if (err.message) {
                errorMessage = err.message
            }
            setState({ data: [], rowCount: 0, users: [] })
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    const collectColumnData = () => {
        const userColumns =
            userPreferences?.dataTableColumns?.users?.userAdmin?.columns

        if (Array.isArray(userColumns)) {
            const mapColumns = userColumns.map((col) => ({
                field: col?.field,
                headerName: col?.headerName,
                width: col?.width,
                isVisible: col?.isVisible,
            }))

            const format = {
                userAdmin: {
                    columns: mapColumns,
                },
            }

            setState({ ...state, userColumns: format })
        }
    }
    const submitColumnData = async () => {
        try {
            setUser({
                key: 'users',
                value: userColumns,
                type: 'columnData',
            })
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleActivation = async () => {
        try {
            let res: any
            if (usersToActivate?.length !== 0) {
                const findUsersToActivate = users
                    .map((user: any) => {
                        if (usersToActivate?.ids?.has(user._id)) {
                            return { _id: user._id, userEmail: user?.userEmail }
                        }
                    })
                    .filter((user) => user !== undefined)

                if (!viewDeactivated) {
                    res = await activateUsers({
                        users: findUsersToActivate,
                        activate: false,
                    })
                } else {
                    res = await activateUsers({
                        users: findUsersToActivate,
                        activate: true,
                    })
                }

                if (res && res.data && res.status === 200) {
                    setToastMessage(
                        `${
                            !viewDeactivated ? 'Deactivated' : 'Activated'
                        } users: ${res.data.map(
                            (userEmail: string, index) => ` ${userEmail}`
                        )}`
                    )
                    getData(!viewDeactivated)
                }
            } else {
                setToastMessage(
                    `No users selected to ${
                        !viewDeactivated ? 'deactivate' : 'activate'
                    }`,
                    'error'
                )
            }
        } catch (error: any) {
            console.error('Error archiving record:', error)
            setToastMessage(error?.message, 'error')
        }
    }

    const handleDeletionData = () => {
        handleDeletion({
            activateData,
            data,
            returnKey: 'deviceSerial',
            setState,
            getData,
            route: deleteUsers,
            viewDeactivated,
            setToastMessage,
            page: 'users',
        })
    }

    const handleDeletionCheck = () => {
        setToastMessage(
            `Are you sure you want to delete this?`,
            'warning',
            handleDeletionData
        )
    }

    const setViewDeactivated = () => {
        setState({ viewDeactivated: !viewDeactivated })
    }

    useEffect(() => {
        getData(true)
        collectColumnData()
        apiSocket?.on('users', (newData) => {
            if (newData) {
                getData(!viewDeactivated)
                setToastMessage(`Users have just updated.`)
            }
        })
    }, [])

    useEffect(() => {
        if (!viewDeactivated) {
            getData(true)
        } else {
            getData(false)
        }
    }, [viewDeactivated])

    useEffect(() => {
        if (noResults) {
            return
        } else {
            getData(true)
        }
    }, [paginationModel?.page, debouncedSearchQuery])

    const setModalVisbility = async (tableRow: any, newData: any) => {
        if (tableRow?.userEmail) {
            getData(true)
            setState({ row: tableRow, open: !open })
        } else {
            getData(true)
            setState({
                row: defaultState.row,
                open: !open,
            })
        }
    }

    const minimizeModal = () => {
        setMinimized(true, {
            title: formCache.userEmail
                ? `Edit: ${formCache.userEmail}`
                : 'Add New User',
            link: 'admin/users',
            data: formCache._id ? formCache : { ...formCache, id: 'newUser' },
            id: formCache._id ? formCache._id : 'newUser',
        })
        setState({ open: false, row: defaultState.row })
    }

    const openFileNamerModal = () => setState({ openFileNamer: !openFileNamer })
    const closeFileNamerModal = () =>
        setState({ openFileNamer: !openFileNamer })
    const handleTextFieldChange = (event: any) =>
        setState({ excelExportName: event.target.value })

    const buttons = [
        {
            buttonName: 'Add New User',
            buttonIcon: <PersonAddIcon />,
            buttonFunction: setModalVisbility,
            disabled: permissionsCheck(userProfilePermissions, 'Users'),
        },

        {
            buttonName: !viewDeactivated ? 'Deactivate' : 'Activate',
            buttonIcon: <ArchiveIcon />,
            buttonFunction: handleActivation,
            disabled: permissionsCheck(userProfilePermissions, 'Users'),
        },
        {
            buttonName: !viewDeactivated
                ? 'View Deactivated Users'
                : 'View Active Users',
            buttonIcon: <Restore />,
            buttonFunction: setViewDeactivated,
            disabled: permissionsCheck(userProfilePermissions, 'Users'),
        },
        {
            buttonName: 'Delete',
            buttonIcon: <DeleteIcon />,
            buttonFunction: handleDeletionCheck,
            disabled: permissionsCheck(userProfilePermissions, 'Super Admin'),
            display: !viewDeactivated ? 'none' : null,
        },
        {
            buttonName: 'Export',
            buttonIcon: <ArrowOutwardIcon />,
            buttonFunction: openFileNamerModal,
        },
    ]

    const defaultColumns = [
        'userEmail',
        'userFirstName',
        'userLastName',
        'userPhoneNumber',
        'updatedBy',
        'activeManufacturer.manufacturerName',
        'assignedDealerProfile',
    ]

    const visibleColumns = [
        'userEmail',
        'userFirstName',
        'userLastName',
        'userPhoneNumber',
        'manufacturerName',
    ]

    const customColumnNames = [
        'Email',
        'First Name',
        'Last Name',
        'Phone Number',
        'Manufacturer',
    ]

    const handleUpdatedColumnsChange = (updatedColumns) => {
        const format = updatedColumns.map((col) => ({
            field: col.field,
            headerName: col.headerName,
            width: col.width,
            isVisible: col.isVisible,
        }))

        const setDensity = state.density

        const setData = {
            userAdmin: { columns: format, density: setDensity },
        }

        setState({
            userColumns: setData,
        })
    }

    const handleDensityChange = (newDensity) => {
        setState({
            density: newDensity,
            userColumns: {
                userAdmin: {
                    ...userAdmin,
                    density: newDensity,
                },
            },
        })
    }

    return (
        <Grid container>
            <Grid container>
                <UniversalToolBar buttons={buttons} />
            </Grid>

            <Grid item xs={12}>
                <UniversalLoadingCircle
                    data={users}
                    reloadFunction={
                        !viewDeactivated
                            ? () => getData(true)
                            : () => getData(false)
                    }
                    customTimeoutMessage={
                        !viewDeactivated
                            ? 'No active users available.'
                            : 'No deactivated users available.'
                    }
                >
                    <UniversalDataTable
                        data={users}
                        apiDensity={density}
                        apiColumns={userColumns?.userAdmin?.columns}
                        savePreferences={() => submitColumnData()}
                        visibleColumns={[]}
                        getRowData={(tableRow) =>
                            setModalVisbility(tableRow, undefined)
                        }
                        onRowSelectionModelChange={(rowSelection) => {
                            setState({
                                usersToActivate: rowSelection,
                                activateData: rowSelection,
                            })
                        }}
                        onUpdatedColumnsChange={handleUpdatedColumnsChange}
                        onDensityChange={handleDensityChange}
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        loading={loading}
                        paginationMode={'server'}
                        pageSizeOptions={[50]}
                        setPaginationModel={(data) => {
                            setState({ paginationModel: data })
                        }}
                        onApiSearch={(text) => {
                            setState({ noResults: false })
                            onApiSearch(text)
                        }}
                    />
                </UniversalLoadingCircle>
            </Grid>

            <UniversalModal
                title={
                    row.id ? `User: ${row.userEmail}` : 'Configure User Account'
                }
                timeStamp={`${
                    row?.id
                        ? ` Modified By: ${row?.updatedBy} ${
                              row?.updatedAt
                                  ? `| Modified Date ${universalDateConverter(
                                        row?.updatedAt
                                    )}`
                                  : ''
                          }  `
                        : ` Created by: ${userEmail} | Modified Date: ${today} `
                }`}
                open={open}
                onClose={(tableRow) => setModalVisbility(tableRow, undefined)}
                wrapperStyle={{
                    margin: 'auto',
                    width: '90%',
                    marginTop: 5,
                }}
                onMinimize={minimizeModal}
                draggable={true}
                minimizable={true}
            >
                <UserForm
                    row={row}
                    formDescription={''}
                    onClose={(tableRow: any, newData?: any) =>
                        setModalVisbility(tableRow, newData)
                    }
                    saveCache={(data: any) => setState({ formCache: data })}
                    minimizeModal={minimizeModal}
                    userFormPermissionCheck={permissionsCheck(
                        userProfilePermissions,
                        'Users'
                    )}
                />
            </UniversalModal>

            <UniversalModal
                title={'New User Registration'}
                open={state.openUserRegistrationModal}
                onClose={toggleUserRegistrationModal}
                wrapperStyle={{
                    margin: 'auto',
                    width: '80%',
                    marginTop: 5,
                }}
            >
                <UserRegistration
                    row={row}
                    onClose={toggleUserRegistrationModal}
                />
            </UniversalModal>

            <UniversalModal
                title={'Download Excel File'}
                open={openFileNamer}
                onClose={closeFileNamerModal}
                fullScreen={false}
                wrapperStyle={{
                    margin: 'auto',
                    width: '80%',
                    marginTop: 5,
                }}
            >
                <FormSection title="To export this Excel file please specify a title ">
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            <Grid item xs={10} sx={{ my: 2 }}>
                                <TextField
                                    required
                                    id="outlined-required"
                                    label="XLXS File Name"
                                    onChange={handleTextFieldChange}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={2}>
                                <Tooltip title="Download Excel File">
                                    <IconButton
                                        onClick={() => {
                                            UniversalExcelExport(
                                                visibleColumns,
                                                customColumnNames,
                                                users,
                                                excelSelectedRows,
                                                `${excelExportName}.xlsx`
                                            )
                                            closeFileNamerModal()
                                        }}
                                    >
                                        <DownloadForOffline
                                            sx={{
                                                fontSize: 70,
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                </FormSection>
            </UniversalModal>
        </Grid>
    )
}

export default UserAdmin
